import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * How often `registration.update()` polls for a new build (architecture
 * §8: "`registration.update()` runs hourly and on window focus"). 080's
 * config registry is this value's eventual home too (`deviceMode.ts`'s
 * `IDLE_UPDATE_MS` gets the identical comment); a plain constant here until
 * that registry exists.
 *
 * Fired on `visibilitychange` → `'visible'` below, not a raw `focus`
 * listener — this task's own brief (task-16-brief.md, Step 2) names
 * `visibilitychange` specifically, which is the more precise event for
 * what actually matters here: a tab coming back to the FOREGROUND is when
 * a stale build is worth re-checking, and it fires reliably for that case
 * without also firing for e.g. a same-window iframe or a form control
 * merely regaining input focus.
 */
const UPDATE_POLL_INTERVAL_MS = 60 * 60 * 1000 // one hour

/**
 * Guards `controllerchange` → reload so it can only ever fire ONCE for the
 * page's whole lifetime. MODULE SCOPE, deliberately, not a `useRef`: the
 * guard must survive independently of any one hook instance's lifecycle,
 * because the failure this exists to prevent — a SECOND reload triggered by
 * a second `controllerchange` for the very update the first reload already
 * picked up — has nothing to do with whether the component that requested
 * the reload is still mounted by the time the second event arrives. This is
 * the same "one store/guard for the page's whole life" shape `pwa/skew.ts`
 * already uses for its own module-scope store, for the analogous reason.
 */
let hasReloadedForUpdate = false

/**
 * Records that THIS page (this module graph — one per real browser tab,
 * since each tab loads its own copy of the JS) is the one that asked for
 * the update, by calling `accept()`. Review round 1, Important 2: in a
 * real browser, activating a waiting worker fires `controllerchange` in
 * EVERY client in scope, not just the tab that clicked accept — measured
 * directly: with no click in a tab, dispatching `controllerchange` reloaded
 * it anyway under the pre-fix code. That is the exact silent mid-form
 * reload architecture §8 forbids by name, just arriving from a SIBLING
 * tab's action instead of this tab's own idle timer.
 *
 * THE COORDINATOR'S RULING (carried here verbatim as the decision record
 * this comment exists to be): gate the reload on this flag. A tab that did
 * not ask shows the banner instead (`waiting` is still `true` there, from
 * the same `updatefound`/`waiting` events every client in scope receives),
 * and its OWN user can accept when ready. This amends architecture §8's
 * literal "accepting reloads on controllerchange" — the counter-argument is
 * real (a tab left on the old build under a new worker can 404 on an
 * old, no-longer-precached lazy chunk) — but Task 12's `lazyArea`
 * (`routes.tsx`) ALREADY catches exactly that failure (a rejected dynamic
 * import), renders `Fallback`, and reports it; the stale-chunk failure this
 * gating creates is already handled, while the mid-form reload it prevents
 * was not. Recorded here, and flagged for the architecture doc as a delta
 * against §8's literal wording, per the coordinator.
 *
 * KNOWN RESIDUAL, not fixed by this round: a tab that never asked, after
 * ANOTHER tab's accept has already activated the worker, is left with a
 * `waiting` banner whose button now does nothing (`registration.waiting`
 * is `null` once activation completes, so `accept()`'s own `if
 * (!waitingWorker) return` silently no-ops). That tab's own affordance
 * should ideally become "already updated elsewhere — reload to catch up"
 * rather than the original "Reload to update"; this round only closes the
 * SAFETY gap (no reload without this tab asking), not that follow-on
 * usability gap, which is smaller and left for a future task.
 */
let requestedSkipWaitingFromThisPage = false

/**
 * The prompt-style half of the client-release contract (architecture §8;
 * `sw.ts`'s own top comment: "the app posts SKIP_WAITING when the user
 * accepts, or when device mode decides it is idle (Task 16)").
 *
 * PLAIN `ServiceWorkerRegistration`/`ServiceWorkerContainer`, not
 * `workbox-window`'s `Workbox` class — installed (Task 13's own
 * dependency) but deliberately unused here. `Workbox` is built to OWN the
 * `register()` call itself (`new Workbox(scriptURL)` then `.register()`);
 * Task 13 already performs that exact call, fire-and-forget, from
 * `main.tsx` (`pwa/register.ts`'s `registerServiceWorker`). Routing this
 * hook through `Workbox` instead would mean either a SECOND, independent
 * registration entry point for the identical `/sw.js` (confusing, and not
 * how either file's own doc comments describe the system), or rewriting
 * `register.ts` to hand `Workbox` the registration job instead — outside
 * this task's declared file list, and not needed: every behaviour this
 * task must cover (exposing `waiting`, `accept()` posting `SKIP_WAITING`,
 * never auto-accepting in browser mode, device-mode auto-accept) is fully
 * expressible over the plain DOM `ServiceWorkerRegistration`/
 * `ServiceWorkerContainer` interfaces this file uses directly.
 *
 * Obtains the registration via `navigator.serviceWorker.getRegistration()`
 * rather than being handed Task 13's own promise — the two resolve to the
 * SAME underlying registration (a `ServiceWorkerContainer` keeps one
 * record per scope, and `register()` is idempotent for a repeated,
 * matching scriptURL/scope), so this stays fully decoupled from
 * `register.ts`: no shared module-scope variable, no import between the
 * two files. `.ready` is tried as a fallback for the narrow first-ever-
 * visit race where this effect's `getRegistration()` call could run before
 * `registerServiceWorker()`'s own `.register()` call has finished setting
 * up the registration record — `.ready` resolves once ANY worker for this
 * scope reaches `active`, which can only happen after that call completes.
 */
export function useUpdatePrompt(): { waiting: boolean; accept: () => void } {
  const [waiting, setWaiting] = useState(false)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    // Mirrors `pwa/register.ts`'s own feature-detection guard exactly — the
    // TS ambient `Navigator` type declares `serviceWorker` unconditionally,
    // but jsdom (this suite's test environment) implements no such
    // property at all, and neither does every real browser this app must
    // still run acceptably in.
    if (!('serviceWorker' in navigator)) return undefined
    const container = navigator.serviceWorker
    let cancelled = false

    function observe(registration: ServiceWorkerRegistration): void {
      registrationRef.current = registration

      // Covers the ordinary case: this hook mounts AFTER a worker already
      // finished installing and is sitting WAITING — true on every reload
      // after the very first one that happens to land after a deploy.
      if (registration.waiting) setWaiting(true)

      // Covers a NEW worker starting to install while this tab stays open.
      // `installed` state plus an EXISTING controller means this install is
      // an UPDATE, not the page's very first-ever install (which has no
      // controller yet to compare against) — the standard workbox/PWA
      // "new content is available" recipe.
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing
        if (!installing) return
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && container.controller) setWaiting(true)
        })
      })
    }

    void (async () => {
      const registration = (await container.getRegistration()) ?? (await container.ready)
      if (!cancelled && registration) observe(registration)
    })()

    function pollForUpdate(): void {
      // Best-effort: `update()` rejecting (offline, a network blip) is not
      // this hook's problem to surface — the next hourly/visibility poll
      // tries again regardless.
      void registrationRef.current?.update()
    }
    const intervalId = setInterval(pollForUpdate, UPDATE_POLL_INTERVAL_MS)
    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') pollForUpdate()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const container = navigator.serviceWorker
    function onControllerChange(): void {
      // Order matters: the "did THIS tab ask" gate first —
      // `requestedSkipWaitingFromThisPage`'s own doc comment has the full
      // reasoning (review round 1, Important 2) — then the "already
      // reloaded once" gate, `hasReloadedForUpdate`'s own doc comment
      // above. Either alone is insufficient: without the first, a sibling
      // tab's accept reloads this tab out from under its user; without the
      // second, a legitimate double `controllerchange` for the ONE update
      // this tab itself accepted would reload it twice.
      if (!requestedSkipWaitingFromThisPage) return
      if (hasReloadedForUpdate) return
      hasReloadedForUpdate = true
      window.location.reload()
    }
    container.addEventListener('controllerchange', onControllerChange)
    return () => container.removeEventListener('controllerchange', onControllerChange)
  }, [])

  const accept = useCallback(() => {
    // Posts the message and nothing else. It never calls `skipWaiting()`
    // itself (`sw.ts`'s own message listener is the only thing that does,
    // and only in response to this exact message) and never reloads itself
    // (the `controllerchange` effect above is what does that, once the
    // waiting worker has genuinely taken control) — this function only
    // ever ASKS.
    const waitingWorker = registrationRef.current?.waiting
    if (!waitingWorker) return
    // Recorded BEFORE the post, not after: `postMessage` is fire-and-forget
    // (no response to await), so there is no "after" that isn't just this
    // same synchronous call — see `requestedSkipWaitingFromThisPage`'s own
    // doc comment for why this flag exists at all.
    requestedSkipWaitingFromThisPage = true
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
  }, [])

  return { waiting, accept }
}

/**
 * Test-only reset for this module's two module-scope flags —
 * `hasReloadedForUpdate` and `requestedSkipWaitingFromThisPage` — following
 * the same plain, pure-export shape this codebase already carries in seven
 * other places (`resetRegistryForTests`, `resetFoldsForTests`,
 * `resetQueriesForTests`, `resetRefusalsForTests`, `resetUpcastersForTests`,
 * `resetCommandsForTests`, `resetShapesForTests`).
 *
 * KEEPS the plain name, unlike its sibling `pwa/skew.ts`'s
 * `resetSkewAndSubscribersForTests` (renamed at web-pre-deploy item 10):
 * this module has no store and no subscribers at all — two module-scope
 * booleans and a `useState`, which is why item 9's `createStore` sweep
 * deliberately left this file alone despite the checklist naming it. There
 * is nothing here that a plain, one-way clear misdescribes.
 *
 * FINAL REVIEW, bucket 1 item 1 — before this export existed, `test/
 * update-prompt.test.tsx` had NO way to clear either flag between cases and
 * worked around it with intra-file ORDERING instead: the describe block
 * covering `requestedSkipWaitingFromThisPage`'s pristine `false` value was
 * required to run FIRST, and the one covering `hasReloadedForUpdate` was
 * required to run LAST, both documented in "MUST run FIRST"/"MUST run LAST"
 * comments. REPRODUCED live: `pnpm vitest --root packages/web run
 * --sequence.shuffle.tests` (seeds 1, 42, 2026) failed with "expected 'spy'
 * to not be called at all, but actually been called 1 times" at line 196
 * (`requestedSkipWaitingFromThisPage` contaminated by an earlier `accept()`)
 * and "expected 'spy' to be called once, but got 0 times" at line 458
 * (`hasReloadedForUpdate` already spent by an earlier reload). A `beforeEach`
 * calling this function is what makes ordering irrelevant instead of
 * load-bearing — see that test file's own top comment for the shuffle
 * results measured after this fix.
 */
export function resetUpdatePromptForTests(): void {
  hasReloadedForUpdate = false
  requestedSkipWaitingFromThisPage = false
}
